import { apiClient } from '../../services/apiClient';
import type {
  CloudMaterial,
  CreateCloudMaterialPayload,
  TemporaryMaterial,
  UpdateCloudMaterialPayload,
  UpdateTemporaryMaterialPayload,
} from './materialsSlice';
import type { AudioReferenceUsage } from '../../components/generation/state/types';
import { isSvgFile, prepareMaterialUploadFile } from './svgLayerUpload';

type ListResponse<T> = {
  items?: T[];
  data?: T[];
  error?: string;
};

/**
 * ファセットデータ（交差絞り込み用）
 * 初回ページのレスポンスにのみ含まれる
 */
export type MaterialFacets = {
  source_types: string[];
  has_no_source_items: boolean;
  aspect_ratios: string[];
  brand_ids: string[];
  has_no_brand_items: boolean;
  has_no_product_items: boolean;
  tag_ids: string[];
  workflow_stage_ids?: string[];
  has_no_workflow_stage_items?: boolean;
  /** ナレーションの生成元シナリオ (音声の一覧のときだけ)。シナリオ以外の条件を掛けた件数で、新しいナレーションがある順 */
  narration_scenarios?: { id: string; title: string; count: number }[];
};

export type PaginatedResponse<T> = {
  items: T[];
  next_cursor: string | null;
  has_more: boolean;
  total_count: number;
  facets?: MaterialFacets;
};

/**
 * 素材フィルタパラメータ
 */
export type MaterialFilters = {
  file_types?: string[];
  source_types?: string[];
  no_source?: boolean;
  asin?: string;
  no_asin?: boolean;
  product_id?: string;
  product_type?: 'amazon' | 'manual';
  no_product?: boolean;
  tag_ids?: string[];
  search?: string;
  aspect_ratios?: string[];
  brand_id?: string;
  no_brand?: boolean;
  workflow_stage_ids?: string[];
  no_workflow_stage?: boolean;
  /** 最大尺(秒)。指定すると duration_seconds <= 値 の素材のみ (画像等は除外) */
  max_duration_seconds?: number;
  /** ナレーションの生成元シナリオ。指定するとそのシナリオで生成したナレーションだけ (他の条件と AND) */
  narration_scenario_id?: string;
};

/**
 * 素材制作進捗 (workflow stage) 型
 */
export type MaterialWorkflowStage = {
  id: string;
  code: string;
  label: string;
  sort_order: number;
  color_hex?: string | null;
  is_custom: boolean;
  stage_id?: string | null;
};

export async function fetchMaterialWorkflowStages(): Promise<MaterialWorkflowStage[]> {
  const { data } = await apiClient.get<{ items: MaterialWorkflowStage[] }>(
    '/materials/workflow-stages',
  );
  return data.items ?? [];
}

export async function fetchTemporaryMaterials(params?: {
  limit?: number;
  cursor?: string | null;
  /** ページ番号ジャンプ用の OFFSET (指定時はカーソルでなく offset ページング) */
  offset?: number | null;
  filters?: MaterialFilters;
}): Promise<PaginatedResponse<TemporaryMaterial>> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.cursor) queryParams.append('cursor', params.cursor);
  if (params?.offset != null) queryParams.append('offset', String(params.offset));

  // フィルタパラメータを追加
  if (params?.filters) {
    const f = params.filters;
    if (f.file_types?.length) {
      f.file_types.forEach((ft) => queryParams.append('file_types', ft));
    }
    if (f.source_types?.length) {
      f.source_types.forEach((s) => queryParams.append('source_types', s));
    }
    if (f.no_source) queryParams.append('no_source', 'true');
    if (f.asin) queryParams.append('asin', f.asin);
    if (f.no_asin) queryParams.append('no_asin', 'true');
    if (f.product_id) queryParams.append('product_id', f.product_id);
    if (f.product_type) queryParams.append('product_type', f.product_type);
    if (f.no_product) queryParams.append('no_product', 'true');
    if (f.tag_ids?.length) {
      f.tag_ids.forEach((t) => queryParams.append('tag_ids', t));
    }
    if (f.search) queryParams.append('search', f.search);
    if (f.aspect_ratios?.length) {
      f.aspect_ratios.forEach((ar) => queryParams.append('aspect_ratios', ar));
    }
    if (f.brand_id) queryParams.append('brand_id', f.brand_id);
    if (f.no_brand) queryParams.append('no_brand', 'true');
    if (f.workflow_stage_ids?.length) {
      f.workflow_stage_ids.forEach((id) => queryParams.append('workflow_stage_ids', id));
    }
    if (f.no_workflow_stage) queryParams.append('no_workflow_stage', 'true');
    if (f.max_duration_seconds != null) queryParams.append('max_duration_seconds', String(f.max_duration_seconds));
    if (f.narration_scenario_id) queryParams.append('narration_scenario_id', f.narration_scenario_id);
  }

  const { data } = await apiClient.get(
    `/materials/temporary${queryParams.toString() ? '?' + queryParams.toString() : ''}`,
  );
  const items = data.items ?? [];
  const mappedItems = items.map((item: TemporaryMaterial) => {
    const previewUrl = item.preview_url || item.previewUrl || item.temp_path;
    return {
      ...item,
      preview_url: previewUrl,
      previewUrl,
    };
  });

  return {
    items: mappedItems,
    next_cursor: data.next_cursor ?? null,
    has_more: data.has_more ?? false,
    total_count: data.total_count ?? 0,
    ...(data.facets ? { facets: data.facets } : {}),
  };
}

/**
 * GCS素材の署名付きURLを取得（有効期限: 1時間）
 * @param materialId 素材ID
 * @param isTemporary trueの場合は個人フォルダ、falseの場合は共有フォルダ
 */
// 署名付きURLのメモリキャッシュ（有効期限: 50分）
const signedUrlCache = new Map<
  string,
  { url: string; durationSeconds?: number; width?: number; height?: number; expiresAt: number }
>();
const SIGNED_URL_CACHE_TTL = 50 * 60 * 1000; // 50分

/**
 * 署名付き URL 応答。動画・音声素材では素材の実尺 (duration_seconds) が付くことがある
 * (バックエンドが素材行から返す。古いアップロードや画像では欠落するので optional)。
 *
 * 動画エディタの BGM トラックは、キューの `sourceDurationSec` (周回の展開・分割の位相計算に要る)
 * をここから補填する (docs/features/video-editor-bgm-track-plan.md §8.2)。
 */
export interface MaterialSignedUrlInfo {
  url: string;
  /** 素材の実尺 (秒)。応答に無ければ undefined */
  durationSeconds?: number;
  /**
   * 素材の実寸 (px)。動画エディタのクロップ (§4.1-J) が `SourceClip.srcWidth` / `srcHeight` に
   * 補うために使う。素材行に列が無い / 0 のときは undefined (両方そろったときだけ付く)。
   */
  width?: number;
  height?: number;
}

/**
 * 署名付き URL を尺付きで取得する。`fetchMaterialSignedUrl` と同じキャッシュ・同じ
 * エンドポイントを使い、応答に `duration_seconds` があれば一緒に返す。
 */
export async function fetchMaterialSignedUrlInfo(
  materialId: string,
  isTemporary: boolean = true,
  opts?: { forceRefresh?: boolean },
): Promise<MaterialSignedUrlInfo> {
  const cacheKey = `${isTemporary ? 'temp' : 'cloud'}:${materialId}`;
  if (!opts?.forceRefresh) {
    const cached = signedUrlCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return {
        url: cached.url,
        ...(cached.durationSeconds != null ? { durationSeconds: cached.durationSeconds } : {}),
        ...(cached.width != null && cached.height != null
          ? { width: cached.width, height: cached.height }
          : {}),
      };
    }
  }

  const endpoint = isTemporary
    ? `/materials/temporary/${materialId}/signed-url`
    : `/materials/cloud/${materialId}/signed-url`;
  const { data } = await apiClient.get(endpoint);
  const rawDuration = data?.duration_seconds;
  const durationSeconds =
    typeof rawDuration === 'number' && Number.isFinite(rawDuration) && rawDuration > 0 ? rawDuration : undefined;
  // 素材の実寸 (動画エディタのクロップ §4.1-J が SourceClip.srcWidth / srcHeight に補う)
  const positiveInt = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : undefined;
  const width = positiveInt(data?.width);
  const height = positiveInt(data?.height);
  const size = width != null && height != null ? { width, height } : {};
  signedUrlCache.set(cacheKey, {
    url: data.url,
    ...(durationSeconds != null ? { durationSeconds } : {}),
    ...size,
    expiresAt: Date.now() + SIGNED_URL_CACHE_TTL,
  });
  return { url: data.url, ...(durationSeconds != null ? { durationSeconds } : {}), ...size };
}

export async function fetchMaterialSignedUrl(
  materialId: string,
  isTemporary: boolean = true,
  opts?: { forceRefresh?: boolean },
): Promise<string> {
  const { url } = await fetchMaterialSignedUrlInfo(materialId, isTemporary, opts);
  return url;
}

/**
 * 素材の署名付きURLキャッシュを破棄する。
 *
 * フォルダ移動 (個人⇔共有) の後にこれを呼ばないと、移動前のフォルダで取得した
 * URL がキャッシュに残り、`fetchMaterialContentBlob` が誤ったフォルダの中継
 * エンドポイントを叩いて 404 になる。
 */
export function invalidateMaterialSignedUrlCache(materialId: string) {
  signedUrlCache.delete(`temp:${materialId}`);
  signedUrlCache.delete(`cloud:${materialId}`);
}

/**
 * 素材の実体を Blob として取得する。
 *
 * まず署名付きURLを直接 fetch し、失敗したら API 経由の中継ダウンロードに
 * フォールバックする。パートナー専用の GCS バケットにはブラウザ用の CORS 設定が
 * 無いことがあり (例: gs://saite-generated)、その場合 fetch(signedUrl) は必ず
 * 失敗する。動画・音声はすべて GCS 由来なので、この経路が無いと ZIP 一括DLや
 * プレビュー動画DLがまるごと成立しない。
 *
 * 個人フォルダ (temporary) → 共有フォルダ (cloud) の順に探す点は署名付きURL取得と同じ。
 */
export async function fetchMaterialContentBlob(materialId: string): Promise<Blob> {
  const folders: Array<'temporary' | 'cloud'> = ['temporary', 'cloud'];

  let lastError: unknown;
  for (const folder of folders) {
    let signedUrl: string | undefined;
    try {
      signedUrl = await fetchMaterialSignedUrl(materialId, folder === 'temporary');
    } catch (error) {
      // このフォルダには存在しない (404) → 次のフォルダへ
      lastError = error;
      continue;
    }

    try {
      const response = await fetch(signedUrl);
      if (!response.ok) throw new Error(`Failed to fetch material: ${response.statusText}`);
      return await response.blob();
    } catch (error) {
      // CORS ブロック / ネットワークエラー。API 経由で中継取得を試す
      lastError = error;
      console.warn(`[Material] Direct fetch failed for ${materialId}, falling back to API proxy:`, error);
    }

    try {
      const { data } = await apiClient.get<Blob>(
        `/materials/${folder}/${materialId}/download`,
        { responseType: 'blob' },
      );
      return data;
    } catch (error) {
      // 中継も失敗 (このフォルダには無い / 署名付きURLがキャッシュ由来で古い等)。
      // ここで throw すると次フォルダを試さずに終わってしまうため、キャッシュを
      // 捨てて次のフォルダへ進む。
      lastError = error;
      invalidateMaterialSignedUrlCache(materialId);
      console.warn(`[Material] API proxy failed for ${materialId} (${folder}), trying next folder:`, error);
    }
  }

  throw lastError instanceof Error ? lastError : new Error(`Material not found: ${materialId}`);
}

export async function fetchCloudMaterials(params?: {
  limit?: number;
  cursor?: string | null;
  /** ページ番号ジャンプ用の OFFSET (指定時はカーソルでなく offset ページング) */
  offset?: number | null;
  filters?: MaterialFilters;
}): Promise<PaginatedResponse<CloudMaterial>> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.cursor) queryParams.append('cursor', params.cursor);
  if (params?.offset != null) queryParams.append('offset', String(params.offset));

  // フィルタパラメータを追加
  if (params?.filters) {
    const f = params.filters;
    if (f.file_types?.length) {
      f.file_types.forEach((ft) => queryParams.append('file_types', ft));
    }
    if (f.source_types?.length) {
      f.source_types.forEach((s) => queryParams.append('source_types', s));
    }
    if (f.no_source) queryParams.append('no_source', 'true');
    if (f.asin) queryParams.append('asin', f.asin);
    if (f.no_asin) queryParams.append('no_asin', 'true');
    if (f.product_id) queryParams.append('product_id', f.product_id);
    if (f.product_type) queryParams.append('product_type', f.product_type);
    if (f.no_product) queryParams.append('no_product', 'true');
    if (f.tag_ids?.length) {
      f.tag_ids.forEach((t) => queryParams.append('tag_ids', t));
    }
    if (f.search) queryParams.append('search', f.search);
    if (f.aspect_ratios?.length) {
      f.aspect_ratios.forEach((ar) => queryParams.append('aspect_ratios', ar));
    }
    if (f.brand_id) queryParams.append('brand_id', f.brand_id);
    if (f.no_brand) queryParams.append('no_brand', 'true');
    if (f.workflow_stage_ids?.length) {
      f.workflow_stage_ids.forEach((id) => queryParams.append('workflow_stage_ids', id));
    }
    if (f.no_workflow_stage) queryParams.append('no_workflow_stage', 'true');
    if (f.max_duration_seconds != null) queryParams.append('max_duration_seconds', String(f.max_duration_seconds));
    if (f.narration_scenario_id) queryParams.append('narration_scenario_id', f.narration_scenario_id);
  }

  const response = await apiClient.get(
    `/materials/cloud${queryParams.toString() ? '?' + queryParams.toString() : ''}`,
  );
  const items = (response.data?.items ?? []) as CloudMaterial[];
  const mappedItems = items.map((item) => {
    const previewUrl = item.preview_url || item.previewUrl || item.storage_path;
    return {
      ...item,
      preview_url: previewUrl,
      previewUrl,
    };
  });

  return {
    items: mappedItems,
    next_cursor: response.data.next_cursor ?? null,
    has_more: response.data.has_more ?? false,
    total_count: response.data.total_count ?? 0,
    ...(response.data.facets ? { facets: response.data.facets } : {}),
  };
}

export async function fetchAsinMaterials(asin: string) {
  const { data } = await apiClient.post<ListResponse<TemporaryMaterial>>('/materials/temporary/fetch-asin', { asin });
  if (data?.error) {
    throw new Error(data.error);
  }
  return data?.items ?? [];
}

export async function saveImageToTemporary(url: string, asin?: string, productLink?: { product_id: string; product_type: string }, source_type?: string): Promise<TemporaryMaterial> {
  const { data } = await apiClient.post<{ item?: TemporaryMaterial; error?: string }>('/materials/temporary/fetch-url', { url, asin, source_type, ...productLink });
  if (data?.error) {
    throw new Error(data.error);
  }
  if (!data.item) {
    throw new Error('Failed to save image to temporary folder');
  }
  return data.item;
}

/** URL のフルページキャプチャを撮影し、個人フォルダの素材として登録する (テンプレートモード用)。 */
export async function captureUrlToTemporary(url: string): Promise<TemporaryMaterial> {
  const { data } = await apiClient.post<{ item?: TemporaryMaterial; error?: string }>('/materials/temporary/capture-url', { url });
  if (data?.error) {
    throw new Error(data.error);
  }
  if (!data?.item) {
    throw new Error('Failed to capture URL');
  }
  return data.item;
}

export async function saveImageToCloud(url: string, asin?: string, productLink?: { product_id: string; product_type: string }): Promise<CloudMaterial> {
  const { data } = await apiClient.post<{ status: string; material?: CloudMaterial; error?: string }>('/materials/cloud/save-from-url', { url, asin, ...productLink });
  if (data?.error) {
    throw new Error(data.error);
  }
  if (!data.material) {
    throw new Error('Failed to save image to cloud');
  }
  return data.material;
}

// ============================================================
// 大容量ファイル直接アップロード (GCS Resumable Direct Upload)
// docs/features/large-file-direct-upload-plan.md v5
// ブラウザ → GCS 直送 (バックエンドにファイルバイトを通さない)
// ============================================================

/** 直接アップロード最大サイズのフォールバック (設定 API 未取得時のみ使用) */
export const DIRECT_UPLOAD_MAX_BYTES_FALLBACK = 2 * 1024 * 1024 * 1024; // 2GB

/** 従来 multipart 経路の実効上限 (Flask MAX_CONTENT_LENGTH 16MB からリクエスト
 *  オーバーヘッド分の余裕を引いた値)。DIRECT_UPLOAD_NOT_READY フォールバック時の
 *  事前チェックに使う。 */
const LEGACY_UPLOAD_MAX_BYTES = 15 * 1024 * 1024; // 15MB

let cachedMaxBytes: number | null = null;

/** バックエンドの MAX_DIRECT_UPLOAD_BYTES を取得する (環境変数で可変のため
 *  フロント定数と乖離しないよう API から同期。セッション中はキャッシュ)。 */
export async function fetchDirectUploadMaxBytes(): Promise<number> {
  if (cachedMaxBytes !== null) return cachedMaxBytes;
  try {
    const { data } = await apiClient.get<{ max_size_bytes: number }>('/materials/upload-sessions/config');
    if (typeof data?.max_size_bytes === 'number' && data.max_size_bytes > 0) {
      cachedMaxBytes = data.max_size_bytes;
      return cachedMaxBytes;
    }
  } catch {
    // 取得失敗時はフォールバック値 (発行 API 側でも検証されるので安全側)
  }
  return DIRECT_UPLOAD_MAX_BYTES_FALLBACK;
}

type UploadSessionResponse = {
  session_id: string;
  upload_url: string;
  expires_at: string;
};

export class DirectUploadError extends Error {
  errorCode?: string;
  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'DirectUploadError';
    this.errorCode = errorCode;
  }
}

/** GCS へ XHR で PUT する (進捗コールバック・AbortSignal 対応)。offset > 0 なら続きから送る。 */
function putToGcs(
  uploadUrl: string,
  file: File,
  offset: number,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DirectUploadError('アップロードがキャンセルされました', 'ABORTED'));
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbortSignal = () => xhr.abort();
    signal?.addEventListener('abort', onAbortSignal, { once: true });
    const cleanup = () => signal?.removeEventListener('abort', onAbortSignal);

    xhr.open('PUT', uploadUrl);
    if (offset > 0) {
      xhr.setRequestHeader('Content-Range', `bytes ${offset}-${file.size - 1}/${file.size}`);
    }
    xhr.upload.onprogress = (event) => {
      if (onProgress && event.lengthComputable) {
        onProgress(Math.min(1, (offset + event.loaded) / file.size));
      }
    };
    xhr.onload = () => {
      cleanup();
      // 200/201 = 完了。308 = まだ途中 (通常チャンク分割時のみ)
      if (xhr.status === 200 || xhr.status === 201) {
        resolve();
      } else {
        reject(new DirectUploadError(`アップロードに失敗しました (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(new DirectUploadError('通信が中断されました'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DirectUploadError('アップロードがキャンセルされました', 'ABORTED'));
    };
    xhr.send(offset > 0 ? file.slice(offset) : file);
  });
}

/** GCS にコミット済みの offset を問い合わせる (resumable プロトコルの再開手順)。
 *  戻り値: 次に送るべき offset (0 = 最初から)。アップロード完了済みなら file.size。 */
async function queryGcsOffset(uploadUrl: string, totalSize: number): Promise<number> {
  const resp = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Range': `bytes */${totalSize}` },
  });
  if (resp.status === 200 || resp.status === 201) {
    return totalSize; // 既に完了している
  }
  if (resp.status === 308) {
    // Range: bytes=0-N (N まで受信済み) / Range ヘッダなし = 未受信
    const range = resp.headers.get('Range');
    if (range) {
      const match = /bytes=0-(\d+)/.exec(range);
      if (match) return parseInt(match[1], 10) + 1;
    }
    return 0;
  }
  throw new DirectUploadError(`アップロードを再開できません (HTTP ${resp.status})`);
}

/** 単一 PUT + 失敗時は offset 照会 → 続きから再送 (最大 maxResumes 回) */
async function putWithResume(
  uploadUrl: string,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
  maxResumes = 3,
): Promise<void> {
  let offset = 0;
  for (let attempt = 0; ; attempt++) {
    try {
      await putToGcs(uploadUrl, file, offset, onProgress, signal);
      return;
    } catch (error) {
      if (error instanceof DirectUploadError && error.errorCode === 'ABORTED') throw error;
      if (attempt >= maxResumes) throw error;
      // 少し待ってから GCS 側のコミット済み offset を確認して続きから再送
      await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
      if (signal?.aborted) {
        throw new DirectUploadError('アップロードがキャンセルされました', 'ABORTED');
      }
      offset = await queryGcsOffset(uploadUrl, file.size);
      if (offset >= file.size) return; // 実は送り切れていた
    }
  }
}

/** GCS resumable session をキャンセルする (ベストエフォート)。
 *  失敗してもサーバー側の期限切れクリーンアップが回収するため throw しない。 */
async function cancelGcsSession(uploadUrl: string): Promise<void> {
  try {
    await fetch(uploadUrl, { method: 'DELETE' });
  } catch {
    // クリーンアップジョブ任せ
  }
}

/**
 * 動画/音声ファイルの直接アップロード (発行 → GCS 直送 PUT → 完了)。
 *
 * バックエンドを経由しないため 2GB (設定 API の上限) まで対応。complete は冪等
 * (再試行しても素材が重複しない)。409 (別リクエスト処理中) は自動再試行する。
 * signal で中断でき、中断時は GCS session へ DELETE を送ってキャンセルする
 * (DELETE が失敗しても期限切れクリーンアップが回収する)。
 */
export async function uploadDirectMaterial(payload: {
  file: File;
  destination: 'temporary' | 'cloud';
  asin?: string | string[];
  tags?: string[];
  notes?: string;
  /** 音声ファイルの既定用途 (docs/features/reference-audio-roles-plan.md §2.5/§2.6)。
   *  音声以外のファイルでは無視される (バックエンドが content_type で検証)。 */
  default_audio_role?: AudioReferenceUsage;
  onProgress?: (fraction: number) => void;
  /** 'uploading' = GCS へ PUT 中 (キャンセル可能) / 'registering' = 素材登録中 (キャンセル不可) */
  onPhase?: (phase: 'uploading' | 'registering') => void;
  signal?: AbortSignal;
}): Promise<TemporaryMaterial | CloudMaterial> {
  const { file, destination, onProgress, onPhase, signal } = payload;

  // SVG はレイヤー素材の解析経路 (multipart) にしか送らない (motion-graphics-mode-plan.md §3.5)。
  // 呼び出し側の isDirectUploadTarget で弾いているが、直送に紛れ込んでも解析を飛ばさないよう二重に守る
  if (isSvgFile(file)) {
    onPhase?.('registering');
    const svgPayload = { file, asin: payload.asin, tags: payload.tags, notes: payload.notes };
    return destination === 'temporary' ? uploadTemporaryMaterial(svgPayload) : uploadCloudMaterial(svgPayload);
  }

  const maxBytes = await fetchDirectUploadMaxBytes();
  if (file.size > maxBytes) {
    throw new DirectUploadError(
      `アップロードできるファイルサイズは最大 ${Math.floor(maxBytes / 1024 / 1024 / 1024)}GB です`,
      'FILE_TOO_LARGE',
    );
  }

  // 1. セッション発行
  let session: UploadSessionResponse;
  try {
    const { data } = await apiClient.post<UploadSessionResponse>('/materials/upload-sessions', {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
      destination,
    });
    session = data;
  } catch (error: any) {
    // つなぎガード: テナントバケットの CORS 準備が未確認のパートナーでは
    // 従来の multipart 経路 (16MB 上限 = 従来と同じ挙動) へフォールバックする
    if (error?.response?.data?.error_code === 'DIRECT_UPLOAD_NOT_READY') {
      if (file.size > LEGACY_UPLOAD_MAX_BYTES) {
        throw new DirectUploadError(
          'この環境は大容量アップロードの準備中のため、現在は16MB以下のファイルのみアップロードできます',
          'DIRECT_UPLOAD_NOT_READY',
        );
      }
      onPhase?.('registering');
      const legacyPayload = {
        file,
        asin: payload.asin,
        tags: payload.tags,
        notes: payload.notes,
        default_audio_role: payload.default_audio_role,
      };
      return destination === 'temporary'
        ? uploadTemporaryMaterial(legacyPayload)
        : uploadCloudMaterial(legacyPayload);
    }
    throw error;
  }

  // 2. GCS へ直接 PUT (進捗表示・中断時は offset 照会で再開)
  onPhase?.('uploading');
  try {
    await putWithResume(session.upload_url, file, onProgress, signal);
    if (signal?.aborted) {
      throw new DirectUploadError('アップロードがキャンセルされました', 'ABORTED');
    }
  } catch (error) {
    if (error instanceof DirectUploadError && error.errorCode === 'ABORTED') {
      // キャンセル: バックエンドのキャンセル API で DB セッション (予約容量) も解放する。
      // GCS session URL への DELETE だけでは pending 行が最大 24 時間残り、
      // その間クォータ予約が解放されず再アップロードが 402 になり得る
      try {
        await apiClient.post(`/materials/upload-sessions/${session.session_id}/cancel`);
      } catch {
        // API が落ちている場合のフォールバック (DB 側は期限切れクリーンアップが回収)
        await cancelGcsSession(session.upload_url);
      }
    }
    throw error;
  }

  // 3 に入ったらキャンセル不可 (サーバー側の登録処理は中断できないため)
  onPhase?.('registering');

  // 3. 完了 (409 = 別リクエスト処理中は待って再試行。200 = 完了済み冪等応答も成功)
  const completeBody = {
    asin: payload.asin,
    tags: payload.tags,
    notes: payload.notes,
    default_audio_role: payload.default_audio_role,
  };
  const maxCompleteRetries = 3;
  for (let attempt = 0; ; attempt++) {
    try {
      const { data } = await apiClient.post<TemporaryMaterial | CloudMaterial>(
        `/materials/upload-sessions/${session.session_id}/complete`,
        completeBody,
      );
      return data;
    } catch (error: any) {
      const status = error?.response?.status;
      const retriable = status === 409 || status === 503;
      if (!retriable || attempt >= maxCompleteRetries) {
        const message = error?.response?.data?.error;
        throw message
          ? new DirectUploadError(message, error?.response?.data?.error_code)
          : error;
      }
      await new Promise((r) => setTimeout(r, 3000 * (attempt + 1)));
    }
  }
}

type UploadMaterialPayload = {
  file: File;
  asin?: string | string[];
  tags?: string[];
  notes?: string;
  source_type?: string;
  /** 音声ファイルの既定用途 (docs/features/reference-audio-roles-plan.md §2.5/§2.6)。
   *  音声以外のファイルでは無視される (バックエンドが content_type で検証)。 */
  default_audio_role?: AudioReferenceUsage;
};

export async function uploadTemporaryMaterial(payload: UploadMaterialPayload) {
  const formData = new FormData();
  // SVG は 16 MB の事前検査と MIME の補完 (features/materials/svgLayerUpload.ts)
  formData.append('file', prepareMaterialUploadFile(payload.file));
  if (payload.asin) {
    // 配列の場合はJSON形式で送信
    formData.append('asin', typeof payload.asin === 'string' ? JSON.stringify(payload.asin) : JSON.stringify(payload.asin));
  }
  if (payload.tags && payload.tags.length > 0) {
    formData.append('tags', JSON.stringify(payload.tags));
  }
  if (payload.notes) {
    formData.append('notes', payload.notes);
  }
  if (payload.source_type) {
    formData.append('source_type', payload.source_type);
  }
  if (payload.default_audio_role) {
    formData.append('default_audio_role', payload.default_audio_role);
  }
  const { data } = await apiClient.post<TemporaryMaterial>('/materials/temporary/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function uploadCloudMaterial(payload: UploadMaterialPayload) {
  const formData = new FormData();
  // SVG は 16 MB の事前検査と MIME の補完 (features/materials/svgLayerUpload.ts)
  formData.append('file', prepareMaterialUploadFile(payload.file));
  if (payload.asin) {
    // 配列の場合はJSON形式で送信
    formData.append('asin', typeof payload.asin === 'string' ? JSON.stringify(payload.asin) : JSON.stringify(payload.asin));
  }
  if (payload.tags && payload.tags.length > 0) {
    formData.append('tags', JSON.stringify(payload.tags));
  }
  if (payload.notes) {
    formData.append('notes', payload.notes);
  }
  if (payload.source_type) {
    formData.append('source_type', payload.source_type);
  }
  if (payload.default_audio_role) {
    formData.append('default_audio_role', payload.default_audio_role);
  }
  const { data } = await apiClient.post<CloudMaterial>('/materials/cloud/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function requestSaveTemporary(materialId: string) {
  const { data } = await apiClient.post('/materials/temporary/' + materialId + '/save');
  // 個人 → 共有 へ移ったので、個人フォルダで取った署名付きURLのキャッシュを捨てる
  invalidateMaterialSignedUrlCache(materialId);
  return data;
}

export type DeleteMaterialError = {
  error: string;
  error_type?: 'material_in_use_by_starred_scenario';
  scenario_titles?: string[];
};

export async function requestDeleteTemporary(materialId: string): Promise<void> {
  try {
    await apiClient.delete('/materials/temporary/' + materialId);
  } catch (error: unknown) {
    // axios error handling
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status: number; data?: DeleteMaterialError } };
      if (axiosError.response?.status === 409 && axiosError.response?.data?.error_type === 'material_in_use_by_starred_scenario') {
        const err = new Error(axiosError.response.data.error) as Error & { scenario_titles?: string[] };
        err.scenario_titles = axiosError.response.data.scenario_titles;
        throw err;
      }
    }
    throw error;
  }
}

export async function updateTemporaryMaterial(materialId: string, payload: UpdateTemporaryMaterialPayload) {
  const { data } = await apiClient.patch<TemporaryMaterial>(`/materials/temporary/${materialId}`, payload);
  return data;
}

export async function createCloudMaterial(payload: CreateCloudMaterialPayload) {
  const { data } = await apiClient.post<CloudMaterial>('/materials/cloud', payload);
  return data;
}

export async function updateCloudMaterial(materialId: string, payload: UpdateCloudMaterialPayload) {
  const { data } = await apiClient.patch<CloudMaterial>(`/materials/cloud/${materialId}`, payload);
  return data;
}

export async function requestDeleteCloudMaterial(materialId: string): Promise<void> {
  try {
    await apiClient.delete(`/materials/cloud/${materialId}`);
  } catch (error: unknown) {
    // axios error handling
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status: number; data?: DeleteMaterialError } };
      if (axiosError.response?.status === 409 && axiosError.response?.data?.error_type === 'material_in_use_by_starred_scenario') {
        const err = new Error(axiosError.response.data.error) as Error & { scenario_titles?: string[] };
        err.scenario_titles = axiosError.response.data.scenario_titles;
        throw err;
      }
    }
    throw error;
  }
}

export type MoveToPersonalResponse = {
  status: 'moved' | 'not_found' | 'error' | 'disabled';
  material?: TemporaryMaterial;
  message?: string;
};

export async function moveCloudMaterialToPersonal(materialId: string): Promise<MoveToPersonalResponse> {
  const { data } = await apiClient.post<MoveToPersonalResponse>(`/materials/cloud/${materialId}/move-to-personal`);
  // 共有 → 個人 へ移ったので、共有フォルダで取った署名付きURLのキャッシュを捨てる
  invalidateMaterialSignedUrlCache(materialId);
  return data;
}

export type TransformPreviewResponse = {
  job_id: string;
  preview_url?: string;
  storage_path?: string;
  width?: number;
  height?: number;
  size_bytes?: number;
  content_type?: string;
};

export type TransformPreviewRequest = {
  preset_id?: string;
  width?: number | null;
  height?: number | null;
  maintain_aspect?: boolean;
  aspect_ratio?: string | null;
  crop?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
  padding?: 'none' | 'white' | 'transparent';
  format?: string;
  quality?: 'low' | 'medium-low' | 'medium' | 'medium-high' | 'high';
};

export async function requestTransformPreview(materialId: string, payload: TransformPreviewRequest) {
  const { data } = await apiClient.post<TransformPreviewResponse>(
    `/materials/cloud/${materialId}/transforms/preview`,
    payload,
  );
  return data;
}

export type TransformSaveResponse = {
  status: 'saved' | 'saved_as';
  material_id?: string;
  material?: CloudMaterial;
  preview_url?: string | null;
};

export async function requestTransformSave(
  materialId: string,
  payload: { job_id?: string; transform_params?: TransformPreviewRequest; overwrite?: boolean },
): Promise<TransformSaveResponse> {
  const { data } = await apiClient.post<TransformSaveResponse>(
    `/materials/cloud/${materialId}/transforms/save`,
    payload,
  );
  return data;
}

export async function requestTransformPreviewTemporary(materialId: string, payload: TransformPreviewRequest) {
  const { data } = await apiClient.post<TransformPreviewResponse>(
    `/materials/temporary/${materialId}/transforms/preview`,
    payload,
  );
  return data;
}

export type TransformSaveResponseTemporary = {
  status: 'saved' | 'saved_as';
  material_id?: string;
  material?: TemporaryMaterial;
  preview_url?: string | null;
};

export async function requestTransformSaveTemporary(
  materialId: string,
  payload: { job_id?: string; transform_params?: TransformPreviewRequest; overwrite?: boolean },
): Promise<TransformSaveResponseTemporary> {
  const { data } = await apiClient.post<TransformSaveResponseTemporary>(
    `/materials/temporary/${materialId}/transforms/save`,
    payload,
  );
  return data;
}

// ============================================================
// ゴミ箱機能 (Trash) API
// ============================================================

/**
 * ゴミ箱フィルタパラメータ
 */
export type TrashFilters = {
  file_type?: 'image' | 'video' | 'audio';
  aspect_ratio?: string;
};

export async function fetchDeletedTemporaryMaterials(params?: { limit?: number; cursor?: string | null; filters?: TrashFilters }): Promise<PaginatedResponse<TemporaryMaterial>> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.cursor) queryParams.append('cursor', params.cursor);

  // フィルタパラメータを追加
  if (params?.filters) {
    if (params.filters.file_type) queryParams.append('file_type', params.filters.file_type);
    if (params.filters.aspect_ratio) queryParams.append('aspect_ratio', params.filters.aspect_ratio);
  }

  const { data } = await apiClient.get(`/materials/temporary/trash${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
  const items = data.items ?? [];
  const mappedItems = items.map((item: TemporaryMaterial) => {
    const previewUrl = item.preview_url || item.previewUrl || item.temp_path;
    return {
      ...item,
      preview_url: previewUrl,
      previewUrl,
    };
  });

  return {
    items: mappedItems,
    next_cursor: data.next_cursor ?? null,
    has_more: data.has_more ?? false,
    total_count: data.total_count ?? 0,
  };
}

export async function fetchDeletedCloudMaterials(params?: { limit?: number; cursor?: string | null; filters?: TrashFilters }): Promise<PaginatedResponse<CloudMaterial>> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.cursor) queryParams.append('cursor', params.cursor);

  // フィルタパラメータを追加
  if (params?.filters) {
    if (params.filters.file_type) queryParams.append('file_type', params.filters.file_type);
    if (params.filters.aspect_ratio) queryParams.append('aspect_ratio', params.filters.aspect_ratio);
  }

  const response = await apiClient.get(`/materials/cloud/trash${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
  const items = (response.data?.items ?? []) as CloudMaterial[];
  const mappedItems = items.map((item) => {
    const previewUrl = item.preview_url || item.previewUrl || item.storage_path;
    return {
      ...item,
      preview_url: previewUrl,
      previewUrl,
    };
  });

  return {
    items: mappedItems,
    next_cursor: response.data.next_cursor ?? null,
    has_more: response.data.has_more ?? false,
    total_count: response.data.total_count ?? 0,
  };
}

export async function restoreTemporaryMaterial(materialId: string): Promise<TemporaryMaterial> {
  const { data } = await apiClient.post<TemporaryMaterial>(`/materials/temporary/${materialId}/restore`);
  return data;
}

export async function restoreCloudMaterial(materialId: string): Promise<CloudMaterial> {
  const { data } = await apiClient.post<CloudMaterial>(`/materials/cloud/${materialId}/restore`);
  return data;
}

export async function permanentlyDeleteTemporaryMaterial(materialId: string): Promise<void> {
  try {
    await apiClient.delete(`/materials/temporary/${materialId}/permanent`);
  } catch (error: unknown) {
    // axios error handling
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status: number; data?: DeleteMaterialError } };
      if (axiosError.response?.status === 409 && axiosError.response?.data?.error_type === 'material_in_use_by_starred_scenario') {
        const err = new Error(axiosError.response.data.error) as Error & { scenario_titles?: string[] };
        err.scenario_titles = axiosError.response.data.scenario_titles;
        throw err;
      }
    }
    throw error;
  }
}

export async function permanentlyDeleteCloudMaterial(materialId: string): Promise<void> {
  try {
    await apiClient.delete(`/materials/cloud/${materialId}/permanent`);
  } catch (error: unknown) {
    // axios error handling
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status: number; data?: DeleteMaterialError } };
      if (axiosError.response?.status === 409 && axiosError.response?.data?.error_type === 'material_in_use_by_starred_scenario') {
        const err = new Error(axiosError.response.data.error) as Error & { scenario_titles?: string[] };
        err.scenario_titles = axiosError.response.data.scenario_titles;
        throw err;
      }
    }
    throw error;
  }
}

export type BulkDeleteResult = {
  deleted_count: number;
  failed_ids: string[];
  protected_ids?: string[];
};

export async function bulkDeleteTemporaryMaterials(ids: string[]): Promise<BulkDeleteResult> {
  const { data } = await apiClient.post<BulkDeleteResult>('/materials/temporary/trash/bulk-delete', { ids });
  return data;
}

export async function bulkDeleteCloudMaterials(ids: string[]): Promise<BulkDeleteResult> {
  const { data } = await apiClient.post<BulkDeleteResult>('/materials/cloud/trash/bulk-delete', { ids });
  return data;
}

export interface MaterialScenario {
  id: string;
  title: string;
  summary?: string;
  source_type: 'asin' | 'custom' | 'manual';
  asin?: string;
  product_id?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export async function fetchMaterialScenarios(materialId: string): Promise<MaterialScenario[]> {
  const { data } = await apiClient.get<{ scenarios: MaterialScenario[] }>(`/materials/${materialId}/scenarios`);
  return data.scenarios ?? [];
}

/**
 * 動画素材から first/last フレームを静止画素材として抽出する。
 * 抽出結果は temporary_materials に保存され、同一動画×同一位置は既存素材が再利用される。
 */
export async function extractVideoFrameMaterial(
  folderType: 'temporary' | 'cloud',
  materialId: string,
  position: 'first' | 'last',
): Promise<TemporaryMaterial> {
  const { data } = await apiClient.post<TemporaryMaterial>(
    `/materials/${folderType}/${materialId}/video-frame`,
    { position },
  );
  return data;
}

/** 実在人物フィルタ対策のサニタイズスタイル。faceless=覆面化 / line_art=線画化 */
export type FacePrivacySanitizeStyle = 'faceless' | 'line_art';

/**
 * 画像素材のサニタイズ画像 (覆面化=顔をAIで消した画像 / 線画=全体をモノクロ線画化)
 * を作成し、元素材へ永続紐付けする。
 *
 * Seedance の実在人物フィルタで指摘された素材に対し、選択中素材カードの
 * 「覆面化する」「線画にする」ボタンから呼ぶ。結果は隠しファイル
 * (source_type='face_privacy_sanitized') として保存され、以降の Seedance 動画生成で
 * バックエンドが自動的に差し替える (両スタイルがあれば最後に実行した方)。
 */
export async function sanitizeMaterialFacePrivacy(
  folderType: 'temporary' | 'cloud',
  materialId: string,
  style: FacePrivacySanitizeStyle = 'faceless',
): Promise<{ sanitized_material: TemporaryMaterial; cached: boolean }> {
  const { data } = await apiClient.post<{ sanitized_material: TemporaryMaterial; cached: boolean }>(
    `/materials/${folderType}/${materialId}/face-privacy-sanitize`,
    { style },
  );
  return data;
}

/**
 * 素材に永続紐付けされたサニタイズ画像 (覆面化/線画の隠しファイル) を取得する。
 * 無ければ null。素材プレビューモーダルの表示トグルに使う。両スタイルがあれば
 * 生成時差し替えと同じ採用規則 (最後に実行したスタイル) の 1 件が返る。
 */
export async function fetchFacePrivacySanitizedMaterial(
  folderType: 'temporary' | 'cloud',
  materialId: string,
): Promise<{ material: TemporaryMaterial; style: FacePrivacySanitizeStyle } | null> {
  const { data } = await apiClient.get<{
    sanitized_material: TemporaryMaterial | null;
    sanitize_style: FacePrivacySanitizeStyle | null;
  }>(
    `/materials/${folderType}/${materialId}/face-privacy-sanitized`,
  );
  if (!data.sanitized_material) return null;
  return { material: data.sanitized_material, style: data.sanitize_style ?? 'faceless' };
}

/**
 * 一時素材を単一取得
 */
export async function fetchTemporaryMaterialById(materialId: string): Promise<TemporaryMaterial | null> {
  try {
    const { data } = await apiClient.get<TemporaryMaterial>(`/materials/temporary/${materialId}`);
    if (data) {
      const previewUrl = data.preview_url || data.previewUrl || data.temp_path;
      return {
        ...data,
        preview_url: previewUrl,
        previewUrl,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * クラウド素材を単一取得
 */
export async function fetchCloudMaterialById(materialId: string): Promise<CloudMaterial | null> {
  try {
    const { data } = await apiClient.get<CloudMaterial>(`/materials/cloud/${materialId}`);
    if (data) {
      const previewUrl = data.preview_url || data.previewUrl || data.storage_path;
      return {
        ...data,
        preview_url: previewUrl,
        previewUrl,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * 素材を単一取得（個人・共有フォルダ両方を検索）
 * 素材が見つからない場合はnullを返す
 */
export async function fetchMaterialById(materialId: string): Promise<TemporaryMaterial | CloudMaterial | null> {
  // まず一時フォルダを検索
  const tempMaterial = await fetchTemporaryMaterialById(materialId);
  if (tempMaterial) {
    return tempMaterial;
  }
  // 見つからなければ共有フォルダを検索
  return fetchCloudMaterialById(materialId);
}

/** 素材と、その置き場 (個人フォルダ = temporary_materials / 共有フォルダ = materials)。 */
export interface MaterialWithFolder {
  material: TemporaryMaterial | CloudMaterial;
  /** 個人フォルダなら true。動画エディタの素材参照 (`materialId` + `isTemporary`) にそのまま使う。 */
  isTemporary: boolean;
}

/**
 * 素材を単一取得し、置き場 (個人 / 共有) も返す。
 *
 * 置き場は BE が付ける `folder_type` で決める。`GET /materials/temporary/<id>` は
 * temporary_materials に無い素材を共有フォルダへフォールバックして 200 で返す (生成履歴の
 * サムネイル保護のため) ので、「temporary の API で取れた = 個人フォルダ」とは判定できない。
 * 共有フォルダの素材を `isTemporary: true` で保存すると、署名 URL の解決が毎回
 * temporary 側で 404 してから cloud 側へ回る (表示はできるが無駄な往復が出る)。
 * `folder_type` が無い (古い BE) ときは従来どおり temporary 扱い。
 */
export async function fetchMaterialWithFolder(materialId: string): Promise<MaterialWithFolder | null> {
  const temporary = await fetchTemporaryMaterialById(materialId);
  if (temporary) {
    return { material: temporary, isTemporary: temporary.folder_type !== 'cloud' };
  }
  const cloud = await fetchCloudMaterialById(materialId);
  return cloud ? { material: cloud, isTemporary: false } : null;
}

// ============================================================
// ラスタ版 (非ラスタ原本 → PNG) の lazy 生成
// ============================================================

export type MaterialFolderType = 'temporary' | 'cloud';

/** `POST /api/materials/<folder_type>/<id>/raster` の 200 応答 */
export type MaterialRasterResponse = {
  raster_url: string;
  raster_status: 'ready';
  width: number | null;
  height: number | null;
  thumbnail_url: string | null;
};

/**
 * 非ラスタ原本 (SVG 等) のラスタ版 (PNG) を取得する。無ければ BE が描画して保存する
 * (docs/features/material-raster-rendition-plan.md §3.1)。
 * 失敗は axios エラーのまま投げる: 422 (恒久失敗・`raster_status:'failed'`) / 502 (一時) / 429 (混雑)。
 * 利用者向けの文言は `error.response.data.error` に入る (ensureRasterAsset.ts の rasterErrorMessage で取り出す)。
 * `force` は失敗済み (failed) の再試行用。
 */
export async function ensureMaterialRaster(
  folderType: MaterialFolderType,
  materialId: string,
  opts?: { force?: boolean },
): Promise<MaterialRasterResponse> {
  const { data } = await apiClient.post<MaterialRasterResponse>(
    `/materials/${folderType}/${materialId}/raster`,
    undefined,
    opts?.force ? { params: { force: 1 } } : undefined,
  );
  return data;
}

/**
 * SVG 素材にレイヤー構成 (モーショングラフィックス用) を付与する。既にあればそのまま返る。
 * ブランド管理のロゴ複製・通常アップロードで入った SVG は入稿解析を通っていないため、
 * モーショングラフィックスの生成ボタン押下時に呼ぶ。応答は素材行 + warnings。
 * エラーは SVG 入稿と同じ形 (400 入稿エラー / 403 MOTION_LAYERED_NOT_AVAILABLE / 429 RATE_LIMITED・RETRY_LATER)。
 */
export async function ensureMaterialLayers(
  folderType: MaterialFolderType,
  materialId: string,
): Promise<TemporaryMaterial & { warnings: string[] }> {
  const { data } = await apiClient.post<TemporaryMaterial & { warnings: string[] }>(
    `/materials/${folderType}/${materialId}/layers`,
  );
  return data;
}

export type LayerRestructureAction = 'expand' | 'collapse';

/**
 * レイヤー構成を作り直す: グループの展開 / 展開したグループに戻す (「レイヤーを確認・編集」モーダル)。
 * 元の SVG を再解析してレイヤーセットを差し替える。応答は素材行 + warnings。展開後はレイヤー番号が変わる。
 */
export async function restructureMaterialLayers(
  folderType: MaterialFolderType,
  materialId: string,
  body: { action: LayerRestructureAction; layer_index: number },
): Promise<TemporaryMaterial & { warnings: string[] }> {
  const { data } = await apiClient.post<TemporaryMaterial & { warnings: string[] }>(
    `/materials/${folderType}/${materialId}/layers/restructure`,
    body,
  );
  return data;
}

// ============================================================
// 素材⇔商品リンク API
// ============================================================

export type ProductLink = {
  id: string;
  material_id: string;
  material_table: 'materials' | 'temporary_materials';
  product_type: 'amazon' | 'manual';
  product_id: string;
  organization_id: string;
  created_at: string;
  product_title?: string;
  asin?: string;
  // 削除済 manual product を指している場合 true (backend enrichment 由来)
  product_deleted?: boolean;
};

export type ProductLinkInput = {
  product_type: 'amazon' | 'manual';
  product_id: string;
};

/**
 * 素材の商品リンク一覧を取得
 */
export async function fetchMaterialProductLinks(
  folderType: 'temporary' | 'cloud',
  materialId: string,
): Promise<ProductLink[]> {
  const { data } = await apiClient.get<{ links: ProductLink[] }>(
    `/materials/${folderType}/${materialId}/product-links`,
  );
  return data.links ?? [];
}

/**
 * 素材に商品リンクを追加
 */
export async function addMaterialProductLinks(
  folderType: 'temporary' | 'cloud',
  materialId: string,
  links: ProductLinkInput[],
): Promise<ProductLink[]> {
  const { data } = await apiClient.post<{ links: ProductLink[] }>(
    `/materials/${folderType}/${materialId}/product-links`,
    { links },
  );
  return data.links ?? [];
}

/**
 * 素材の商品リンクを全置換（旧リンクを全削除して新リンクを設定）
 */
export async function replaceMaterialProductLinks(
  folderType: 'temporary' | 'cloud',
  materialId: string,
  links: ProductLinkInput[],
): Promise<ProductLink[]> {
  const { data } = await apiClient.put<{ links: ProductLink[] }>(
    `/materials/${folderType}/${materialId}/product-links`,
    { links },
  );
  return data.links ?? [];
}

/**
 * 素材から商品リンクを削除
 */
export async function removeMaterialProductLinks(
  folderType: 'temporary' | 'cloud',
  materialId: string,
  links: ProductLinkInput[],
): Promise<number> {
  const { data } = await apiClient.delete<{ deleted: number }>(
    `/materials/${folderType}/${materialId}/product-links`,
    { data: { links } },
  );
  return data.deleted ?? 0;
}

/**
 * 商品に紐づく素材リンク一覧を取得（マニュアル商品用）
 */
export async function fetchProductMaterials(
  productId: string,
  materialTable?: 'materials' | 'temporary_materials',
): Promise<ProductLink[]> {
  const params = new URLSearchParams();
  if (materialTable) params.append('material_table', materialTable);
  const qs = params.toString();
  const { data } = await apiClient.get<{ links: ProductLink[]; total: number }>(
    `/products/${productId}/materials${qs ? '?' + qs : ''}`,
  );
  return data.links ?? [];
}

// ============================================================
// 絵コンテの境界調整モーダル用: フレームのストリップとカット検出
// (docs/features/brand-cut-motion-graphics-plan.md §10.13.1 契約 F。ポイント不要・組織スコープ・動画のみ)
// ============================================================

/** `GET /api/materials/<id>/frames` の 1 コマ (元動画の fps のフレーム番号・秒・長辺 240px の JPEG data URL)。 */
export interface MaterialFrameStripFrame {
  frame: number;
  sec: number;
  jpeg_data_url: string;
}

export interface MaterialFrameStripResponse {
  fps: { num: number; den: number };
  duration_sec: number;
  frames: MaterialFrameStripFrame[];
}

/**
 * 動画素材の推定位置 (at_sec) の前後のフレーム画像を 1 回で取る (最大 25 枚・before / after は各 12 まで)。
 * 署名 URL の動画は canvas で読めないため backend の ffmpeg が切る。
 */
export async function fetchMaterialFrames(
  materialId: string,
  params: { atSec: number; before?: number; after?: number },
): Promise<MaterialFrameStripResponse> {
  const { data } = await apiClient.get<MaterialFrameStripResponse>(`/materials/${materialId}/frames`, {
    params: {
      at_sec: Math.max(0, params.atSec),
      before: params.before ?? 12,
      after: params.after ?? 12,
    },
  });
  return data;
}

/** `GET /api/materials/<id>/scene-changes` の検出カット (フレーム番号・秒・ffmpeg の scene スコア)。 */
export interface MaterialSceneCut {
  frame: number;
  sec: number;
  score: number;
}

export interface MaterialSceneChangesResponse {
  fps: { num: number; den: number };
  cuts: MaterialSceneCut[];
  /** 素材行のキャッシュから返した (2 回目以降)。 */
  cached?: boolean;
}

/** 動画素材のカット (場面の切り替わり) を検出して返す。結果は素材行にキャッシュされる。 */
export async function fetchMaterialSceneChanges(materialId: string): Promise<MaterialSceneChangesResponse> {
  const { data } = await apiClient.get<MaterialSceneChangesResponse>(`/materials/${materialId}/scene-changes`);
  return data;
}

/** `POST /api/materials/<id>/extract-audio` の応答 (動画素材から取り出した音声素材)。 */
export interface ExtractedAudioMaterial {
  material_id: string;
  is_temporary: boolean;
  name: string | null;
  duration_seconds: number | null;
  /** true = 同じ動画から前に取り出した音声素材を使い回した (1組織1回で冪等) */
  reused: boolean;
}

/**
 * 動画素材の音声だけを取り出し、個人フォルダの音声素材 (既定用途 = 効果音) として複製する
 * (docs/features/video-editor-capability-gaps-plan.md §4.3-D「動画素材からの音声抽出」)。
 * 動画エディタの効果音ピッカーで動画素材を選んだときに使う。失敗は axios エラーのまま投げる
 * (400 動画以外・音声なし / 402 容量超過 / 502 取得・抽出の失敗。文言は `error.response.data.error`)。
 */
export async function extractAudioFromVideoMaterial(materialId: string): Promise<ExtractedAudioMaterial> {
  const { data } = await apiClient.post<ExtractedAudioMaterial>(`/materials/${materialId}/extract-audio`);
  return data;
}
